// Живое состояние интерфейса: базовые справочники + поток событий SSE (/api/stream).
// Страницы держат свои данные сами и подписываются на события через useStream / useReconnect.
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { ApiError, api } from './api.ts'
import { isDoneUnseen } from './lib/outcome.ts'
import { notifyEscalation, notifyOwnerReply, notifyTaskDone } from './notify.ts'
import type {
  Agent,
  CatalogResponse,
  Direction,
  Runner,
  ServiceCard,
  Settings,
  SettingsResponse,
  Stats,
  StreamEvent,
} from './types.ts'

export interface LiveState {
  connected: boolean
  stats: Stats | null
  settings: Settings | null
  runners: Runner[]
  agents: Agent[]
  agentsLoaded: boolean
  directions: Direction[]
  services: ServiceCard[]
  catalogLoaded: boolean
  /** OMA выкатили после открытия вкладки — новых карточек и кнопок здесь нет, нужно обновить страницу */
  stale: boolean
}

const initial: LiveState = {
  connected: false,
  stats: null,
  settings: null,
  runners: [],
  agents: [],
  agentsLoaded: false,
  directions: [],
  services: [],
  catalogLoaded: false,
  stale: false,
}

let state: LiveState = initial
const listeners = new Set<() => void>()

function setState(patch: Partial<LiveState>): void {
  state = { ...state, ...patch }
  for (const l of listeners) l()
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function getLive(): LiveState {
  return state
}

/** Селектор должен возвращать уже существующую ссылку или примитив. */
export function useLive<T>(selector: (s: LiveState) => T): T {
  return useSyncExternalStore(subscribe, () => selector(state))
}

export function useAgentMap(): Map<string, Agent> {
  const agents = useLive((s) => s.agents)
  return useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents])
}

export function useDirectionMap(): Map<string, Direction> {
  const dirs = useLive((s) => s.directions)
  return useMemo(() => new Map(dirs.map((d) => [d.id, d])), [dirs])
}

export function useServiceMap(): Map<string, ServiceCard> {
  const services = useLive((s) => s.services)
  return useMemo(() => new Map(services.map((x) => [x.id, x])), [services])
}

// ───────── загрузка справочников ─────────

export async function refreshStats(): Promise<void> {
  try {
    setState({ stats: await api.get<Stats>('/api/stats') })
  } catch {
    /* индикаторы просто не обновятся */
  }
}

export async function refreshSettings(): Promise<void> {
  try {
    const r = await api.get<SettingsResponse>('/api/settings')
    setState({ settings: r.settings, runners: r.runners ?? [] })
  } catch {
    /* noop */
  }
}

export async function refreshAgents(): Promise<void> {
  try {
    setState({ agents: await api.get<Agent[]>('/api/agents'), agentsLoaded: true })
  } catch {
    /* noop */
  }
}

export async function refreshCatalog(): Promise<void> {
  try {
    const c = await api.get<CatalogResponse>('/api/catalog')
    setState({ directions: sortDirections(c.directions ?? []), services: c.services ?? [], catalogLoaded: true })
  } catch {
    /* noop */
  }
}

export function sortDirections(d: Direction[]): Direction[] {
  return [...d].sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name, 'ru'))
}

/** Свежие данные пришли из другого ответа (например, /api/board). */
export function setDirections(d: Direction[]): void {
  if (d.length) setState({ directions: sortDirections(d) })
}
export function setAgents(a: Agent[]): void {
  setState({ agents: a, agentsLoaded: true })
}
export function upsertAgent(a: Agent): void {
  const i = state.agents.findIndex((x) => x.id === a.id)
  if (i === -1) setState({ agents: [...state.agents, a] })
  else {
    const next = [...state.agents]
    next[i] = { ...next[i]!, ...a }
    setState({ agents: next })
  }
}
export function setSettings(s: Settings): void {
  setState({ settings: s })
}

/** Сверить сборку вкладки с выкаченной: после выката старая вкладка не знает новых карточек и кнопок. */
async function checkBuild(): Promise<void> {
  if (state.stale) return
  try {
    // при разработке (vite) /healthz отдаёт не JSON — проверка молча пропускается
    const r = await fetch('/healthz', { cache: 'no-store' })
    const b = (await r.json()) as { build?: string | null }
    if (b.build && b.build !== __OMA_BUILD__) setState({ stale: true })
  } catch {
    /* сервер перезапускается — проверим при следующем подключении */
  }
}

function loadBase(): void {
  void refreshStats()
  void refreshSettings()
  void refreshAgents()
  void refreshCatalog()
}

function debounce(fn: () => void, ms: number): () => void {
  let t: number | undefined
  return () => {
    window.clearTimeout(t)
    t = window.setTimeout(fn, ms)
  }
}

// фоновая вкладка не перечитывает данные на каждое событие агентов: только отмечает «устарело»
// и читает один раз, когда её откроют (несколько забытых вкладок держали сервер занятым)
const staleWhileHidden = new Set<() => void>()
export function whenVisible(fn: () => void): void {
  if (document.visibilityState === 'hidden') staleWhileHidden.add(fn)
  else fn()
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return
  const due = [...staleWhileHidden]
  staleWhileHidden.clear()
  for (const fn of due) fn()
})

const loadStats = () => void refreshStats()
const loadAgents = () => void refreshAgents()
const statsSoon = debounce(() => whenVisible(loadStats), 700)
const agentsSoon = debounce(() => whenVisible(loadAgents), 1500)

// ───────── поток SSE ─────────

type StreamHandler = (ev: StreamEvent) => void
const streamHandlers = new Set<StreamHandler>()
const reconnectHandlers = new Set<() => void>()

const EVENT_TYPES: StreamEvent['type'][] = [
  'task',
  'message',
  'job',
  'job_event',
  'escalation',
  'agent',
  'activity',
  'intent',
  'change',
  'settings',
  'runner',
]

let es: EventSource | null = null
let started = false
let wasConnected = false
let retryTimer: number | undefined
let retryDelay = 1000
let statsTimer: number | undefined

export function startLive(): void {
  if (started) return
  started = true
  wasConnected = false
  loadBase()
  connect()
  statsTimer = window.setInterval(() => whenVisible(loadStats), 30_000)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkBuild()
  })
}

export function stopLive(): void {
  started = false
  window.clearTimeout(retryTimer)
  window.clearInterval(statsTimer)
  es?.close()
  es = null
  state = initial
  for (const l of listeners) l()
}

function connect(): void {
  if (!started) return
  es?.close()
  const source = new EventSource('/api/stream', { withCredentials: true })
  es = source
  source.onopen = () => {
    if (es !== source) return
    retryDelay = 1000
    setState({ connected: true })
    // выкат перезапускает сервер: поток переподключается — тут и видно, что сборка сменилась
    void checkBuild()
    if (wasConnected) {
      // после разрыва — перечитать всё, что могли пропустить
      loadBase()
      for (const h of reconnectHandlers) h()
    }
    wasConnected = true
  }
  source.onerror = () => {
    if (es !== source) return
    if (state.connected) setState({ connected: false })
    if (source.readyState === EventSource.CLOSED) {
      // браузер сдался (например, 401/502) — переподключаемся сами
      source.close()
      es = null
      scheduleReconnect()
    }
  }
  for (const type of EVENT_TYPES) {
    source.addEventListener(type, (e) => handleRaw(type, (e as MessageEvent<string>).data))
  }
}

function scheduleReconnect(): void {
  if (!started) return
  window.clearTimeout(retryTimer)
  const delay = retryDelay
  retryDelay = Math.min(retryDelay * 2, 15_000)
  retryTimer = window.setTimeout(async () => {
    try {
      await api.get('/api/auth/me')
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return // auth переключит на экран входа
    }
    connect()
  }, delay)
}

function handleRaw(type: StreamEvent['type'], raw: string): void {
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return
  }
  const ev = { type, data } as StreamEvent
  applyInternal(ev)
  for (const h of streamHandlers) {
    try {
      h(ev)
    } catch (err) {
      console.error('stream handler failed', err)
    }
  }
}

function applyInternal(ev: StreamEvent): void {
  switch (ev.type) {
    case 'settings':
      setState({ settings: ev.data })
      break
    case 'runner': {
      const i = state.runners.findIndex((r) => r.id === ev.data.id)
      const next = [...state.runners]
      if (i === -1) next.push(ev.data)
      else next[i] = ev.data
      setState({ runners: next })
      statsSoon()
      break
    }
    case 'agent':
      upsertAgent(ev.data)
      statsSoon()
      break
    case 'job':
      agentsSoon()
      statsSoon()
      break
    case 'escalation':
      notifyEscalation(ev.data)
      statsSoon()
      break
    case 'message':
      if (ev.data.to_type === 'human' && ev.data.from_type === 'agent') {
        notifyOwnerReply(ev.data, state.agents.find((a) => a.id === ev.data.from_id)?.name ?? ev.data.from_id)
      }
      break
    case 'task':
      if (isDoneUnseen(ev.data)) notifyTaskDone(ev.data)
      statsSoon()
      break
    case 'intent':
      statsSoon()
      break
    default:
      break
  }
}

/** Подписка компонента на события потока; обработчик может меняться между рендерами. */
export function useStream(handler: StreamHandler): void {
  const ref = useRef(handler)
  useEffect(() => {
    ref.current = handler
  })
  useEffect(() => {
    const h: StreamHandler = (ev) => ref.current(ev)
    streamHandlers.add(h)
    return () => {
      streamHandlers.delete(h)
    }
  }, [])
}

/** Вызвать fn после восстановления связи (чтобы перечитать данные). */
export function useReconnect(fn: () => void): void {
  const ref = useRef(fn)
  useEffect(() => {
    ref.current = fn
  })
  useEffect(() => {
    const h = () => ref.current()
    reconnectHandlers.add(h)
    return () => {
      reconnectHandlers.delete(h)
    }
  }, [])
}

// ───────── общие «часы» для относительного времени ─────────

let now = Date.now()
const tickListeners = new Set<() => void>()
window.setInterval(() => {
  now = Date.now()
  for (const l of tickListeners) l()
}, 30_000)

function subscribeTick(fn: () => void): () => void {
  tickListeners.add(fn)
  return () => {
    tickListeners.delete(fn)
  }
}

/** Текущее время, обновляется раз в 30 секунд (для «5 мин назад»). */
export function useNow(): number {
  return useSyncExternalStore(subscribeTick, () => now)
}
